import { useEffect, useState } from 'react';

import {
  ENTRADA,
  formatearImporte,
  interpretarImporte,
  mensajeDeEntrada,
  valorPelado,
} from './formatoDeImporte.js';

/**
 * CAMPO DE IMPORTE ÚNICO DE TODA LA APLICACIÓN (RF-086, D-82).
 *
 * Se comporta igual en todas partes:
 *   * RN-135 · al enfocarlo se SELECCIONA todo su contenido y se muestra el número
 *     pelado, así que escribir un dígito sustituye el valor sin pelear con el
 *     `0.00`. Vale para toque, clic y tabulador.
 *   * RN-146h / RN-137 · MIENTRAS SE ESCRIBE, EL CAMPO NO SE FORMATEA NI SE
 *     NORMALIZA: muestra exactamente lo tecleado. El formato de RN-146 se aplica
 *     SOLO al perder el foco, y al volver a enfocar reaparece el número editable.
 *     Vacío se queda VACÍO: no se inventa un valor.
 *   * RN-148 · un texto pegado con código de divisa DISTINTO al del contexto no se
 *     adopta: el campo conserva lo pegado, se marca inválido y el mensaje nombra
 *     las dos divisas.
 *   * RN-149 · el signo menos no se teclea —la tecla no entra y se explica por
 *     qué— y pegado se rechaza.
 *   * RN-139b / RN-139c · más de dos decimales y texto no numérico se rechazan sin
 *     recortar en silencio.
 *
 * DEFECTO CORREGIDO EL 2026-08-31 (hallazgo de Deisy Paez en el celular).
 * Este campo mostraba durante la edición un valor DERIVADO —`valorPelado(valor)`,
 * que rellena a dos decimales—, no lo que la persona acababa de teclear. Como el
 * texto pintado no coincidía con el del navegador, React reescribía el valor del
 * DOM en CADA pulsación y el punto de inserción saltaba al final; la tecla
 * siguiente caía después del punto decimal. Por eso teclear «568» terminaba en
 * `5.008` —el último dígito quedaba como tercer decimal— y al salir del campo se
 * rechazaba por decimales. La corrección es la regla, no un parche: mientras hay
 * foco manda `textoEnEdicion`, que es literalmente lo tecleado, y NADA lo
 * reescribe hasta que el campo pierde el foco.
 *
 * EL VALOR QUE SUBE AL FORMULARIO sigue siendo cadena decimal (RNF-001) y se
 * normaliza al salir. El formato es presentación y no altera ningún importe ya
 * guardado (RN-146g).
 */
export function CampoDeImporte({
  id,
  valor,
  alCambiar,
  codigoDivisa,
  contexto = 'este campo',
  alCambiarValidez,
  ayuda = null,
  disabled = false,
  ...resto
}) {
  const [enEdicion, setEnEdicion] = useState(false);
  const [error, setError] = useState(null);
  // TEXTO TAL COMO SE ESTÁ TECLEANDO. Es la única fuente de lo que se ve mientras
  // el campo tiene el foco: sin esto, cada pulsación se reescribía y el cursor
  // saltaba.
  const [textoEnEdicion, setTextoEnEdicion] = useState('');
  // Lo que la persona tiene delante cuando el valor NO se adoptó: RN-148b exige
  // que el campo conserve lo pegado en vez de borrarlo o corregirlo solo.
  const [textoRechazado, setTextoRechazado] = useState(null);

  useEffect(() => { alCambiarValidez?.(error === null); }, [error, alCambiarValidez]);

  const textoVisible = textoRechazado !== null
    ? textoRechazado
    : (enEdicion ? textoEnEdicion : presentar(valor, codigoDivisa));

  function alEnfocar(evento) {
    // Se arranca desde el número pelado —sin símbolo, sin miles y sin código— y a
    // partir de ahí manda lo que se teclee (RN-146h).
    setTextoEnEdicion(textoRechazado !== null ? textoRechazado : valorPelado(valor ?? ''));
    setEnEdicion(true);
    setTextoRechazado(null);
    setError(null);
    // El `select` va diferido: en móvil el navegador coloca el cursor DESPUÉS del
    // foco, y sin esperar al siguiente ciclo la selección se pierde (RN-135).
    const elemento = evento.target;
    setTimeout(() => { try { elemento.select(); } catch { /* campo ya desmontado */ } }, 0);
  }

  function alTeclear(evento) {
    // RN-149b: el carácter no entra. No se sustituye ni se suaviza la validación
    // del servidor; simplemente aquí no se captura un negativo.
    if (evento.key === '-' || evento.key === '−') {
      evento.preventDefault();
      setError(mensajeDeEntrada({ estado: ENTRADA.NEGATIVA }, codigoDivisa, contexto));
    }
  }

  function alEscribir(evento) {
    const tecleado = evento.target.value;
    setTextoRechazado(null);
    setError(null);
    // Se pinta EXACTAMENTE lo tecleado: ni se rellenan decimales, ni se agrupan
    // miles, ni se recorta nada. Corregir a media captura —borrar, insertar en
    // medio, escribir el punto— tiene que funcionar como en cualquier campo.
    setTextoEnEdicion(tecleado);
    // El valor sube en crudo para que el borrador guarde cada tecla (RN-144e). Se
    // normaliza al perder el foco, no antes.
    alCambiar(tecleado);
  }

  function alPegar(evento) {
    const pegado = evento.clipboardData?.getData('text') ?? '';
    const resultado = interpretarImporte(pegado, codigoDivisa);
    if (resultado.estado === ENTRADA.VALIDA || resultado.estado === ENTRADA.VACIA) return;

    // El campo NO se modifica con un valor que no se acepta: conserva lo pegado a
    // la vista, marcado en rojo, y la persona teclea el importe (RN-148b).
    evento.preventDefault();
    setTextoRechazado(pegado);
    setError(mensajeDeEntrada(resultado, codigoDivisa, contexto));
  }

  function alSalir() {
    setEnEdicion(false);
    if (textoRechazado !== null) return; // Se conserva el rechazo tal cual.

    // AQUÍ, Y SOLO AQUÍ, se normaliza y se formatea (RN-136, RN-137).
    const resultado = interpretarImporte(textoEnEdicion, codigoDivisa);
    if (resultado.estado === ENTRADA.VACIA) {
      setError(null);
      alCambiar('');
      return;
    }
    if (resultado.estado === ENTRADA.VALIDA) {
      setError(null);
      alCambiar(resultado.valor);
      return;
    }
    setTextoRechazado(textoEnEdicion);
    setError(mensajeDeEntrada(resultado, codigoDivisa, contexto));
  }

  return (
    <>
      <input
        {...resto}
        id={id}
        type="text"
        inputMode="decimal"
        disabled={disabled}
        value={textoVisible}
        aria-invalid={error !== null}
        data-prueba="campo-de-importe"
        data-divisa={codigoDivisa ?? ''}
        onFocus={alEnfocar}
        onKeyDown={alTeclear}
        onChange={alEscribir}
        onPaste={alPegar}
        onBlur={alSalir}
      />
      {error && <span className="err" data-prueba="error-de-importe">{error}</span>}
      {!error && ayuda && <span className="ayuda">{ayuda}</span>}
    </>
  );
}

/** Un campo vacío se presenta vacío, no como cero ni como raya (RN-136). */
function presentar(valor, codigoDivisa) {
  if (valor === null || valor === undefined || String(valor).trim() === '') return '';
  return formatearImporte(valor, codigoDivisa);
}
